// components/layout/Sidebar.tsx
import { useDarkTheme } from "@/contexts/DarkTheme";
import { useSidebarContext } from "@/contexts/SidebarContext";
import { useIsMobile } from "@/helpers/helperFunctions";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@react-hook/media-query";
import {
  ChartNoAxesGantt,
  Gauge,
  Rocket,
  Unplug,
  PanelLeftClose,
  PanelLeftOpen,
  CreditCard,
  HelpCircle,
  SquarePen,
  PanelLeft,
} from "lucide-react";
import { signOut } from "next-auth/react";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState, ReactNode, useRef } from "react";
import { BsBarChart } from "react-icons/bs";
import { IoCreateOutline } from "react-icons/io5";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/contexts/SubscriptionContext";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "../../ui/menubar";
import {
  brokerConnectModalAtom,
  technicalSupportModalAtom,
} from "@/Atoms/modal/DialogueAtoms";
import { useRecoilState } from "recoil";
import { motion, AnimatePresence } from "framer-motion";
import { SidebarCarousel } from "../common/SideBarCarousal";
import { useNotificationSocket } from "@/contexts/NotificationSocketContext";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { useUserContext } from "@/contexts/userContext";
import { Button } from "@/components/ui/button";
import { FiSidebar } from "react-icons/fi";

interface SidebarItemProps {
  href?: string;
  icon: ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
  target?: string;
}

function SidebarItem({
  href,
  icon,
  label,
  active,
  onClick,
  target,
}: SidebarItemProps) {
  const { showSidebar, setShowSidebar } = useSidebarContext();
  const { t } = useTranslation("layout-page");
  const isMobile = useIsMobile(false);
  const handleClick = () => {
    if (isMobile) setShowSidebar(false);
    onClick?.();
  };

  const content = (
    <li
      onClick={handleClick}
      className={cn(
        "flex gap-3 items-center text-sm rounded-[6px] px-4  py-2 capitalize cursor-pointer hover:bg-primary hover:text-primary-foreground",
        active && "bg-primary text-primary-foreground",
      )}
    >
      <div className="flex items-center justify-center w-5 h-5 shrink-0">
        {icon}
      </div>
      <motion.div
        animate={{
          opacity: showSidebar ? 1 : 0,
        }}
        transition={{ duration: 0.2 }}
        className="overflow-hidden whitespace-nowrap"
      >
        {label}
      </motion.div>
    </li>
  );

  return href ? (
    <Link href={href} target={target} className="w-full">
      <HoverCard openDelay={0}>
        <HoverCardTrigger>{content}</HoverCardTrigger>
        {!showSidebar && (
          <HoverCardContent className="w-auto ml-[52px] mt-[-20px] p-2 font-medium z-[1000]">
            {label}
          </HoverCardContent>
        )}
      </HoverCard>
    </Link>
  ) : (
    <HoverCard openDelay={0}>
      <HoverCardTrigger>{content}</HoverCardTrigger>
      {!showSidebar && (
        <HoverCardContent className="w-auto ml-[52px] mt-[-20px] p-2 font-medium z-[1000]">
          {t(label)}
        </HoverCardContent>
      )}
    </HoverCard>
  );
}

export default function SideBar({ showDisabled }: { showDisabled?: boolean }) {
  const { t } = useTranslation("layout-page");
  const { pathname, query, push, reload } = useRouter();
  const { showSidebar, setShowSidebar } = useSidebarContext();
  const { darkTheme } = useDarkTheme();
  const matchesMobile = useMediaQuery(`only screen and (max-width:  1024px)`);
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom,
  );
  const [showTechnicalRequest, setShowTechnicalRequest] = useRecoilState(
    technicalSupportModalAtom,
  );
  const { user } = useUserContext();

  const isMobile = useIsMobile(false);
  const { socket } = useNotificationSocket();
  const [headerHeight, setHeaderHeight] = useState(0);
  const rafRef = useRef<number>();
  const { details } = useAppDetails();
  const { userType } = useSubscription();

  useEffect(() => {
    const header = document.getElementById("main-header");
    if (!header) return;

    const observer = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setHeaderHeight(header.offsetHeight + 5);
      });
    });

    observer.observe(header);
    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Popup logic
  const [showPopup, setShowPopup] = useState(false);
  useEffect(() => {
    const cookieValue = document.cookie
      .split("; ")
      .find((row) => row.startsWith("changelog_v1="))
      ?.split("=")[1];
    if (cookieValue !== "1") setShowPopup(false);
  }, []);

  useEffect(() => {
    document.body.style.overflow =
      showSidebar && matchesMobile ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showSidebar, matchesMobile]);

  useEffect(() => {
    if (matchesMobile) {
      setShowSidebar(false);
    }
  }, [matchesMobile]);

  const Logout = () => {
    socket?.close();
    Object.keys(localStorage).forEach((key) => {
      if (key !== "app-details" && !key.startsWith("welcome_pm_modal")) {
        localStorage.removeItem(key);
      }
    });
    signOut({ redirect: false });
    push({ pathname: "/logout", query }).then(() => reload());
  };

  return (
    <>
      {/* Sidebar */}
      <motion.nav
        initial={{ x: matchesMobile ? -200 : 0, width: showSidebar ? 222 : 70 }}
        animate={{
          x: showSidebar ? 0 : matchesMobile ? -200 : 0,
          width: showSidebar ? 222 : 70,
        }}
        transition={{ duration: 0.15, ease: "easeInOut" }}
        className={cn(
          "p-4 lg:block fixed left-0 z-20 border-r border-border/50",
          darkTheme ? "bg-card shadow-md" : "bg-white",
          showDisabled &&
            "hidden lg:block lg:pointer-events-none lg:opacity-50",
          isMobile && "h-full ",
        )}
        style={{
          top: matchesMobile ? `${headerHeight}px` : "0px",
          height: matchesMobile ? `calc(100vh - ${headerHeight}px)` : "100vh",
        }}
      >
        <div
          className="flex flex-col justify-between"
          style={{
            height: matchesMobile
              ? `calc(100vh - ${headerHeight + 15}px)`
              : "calc(100vh - 40px)",
          }}
        >
          <div>
            <div className="relative flex items-center h-12 mb-4 px-2 justify-between">
              {showSidebar ? (
                <>
                  <Link
                    href={{
                      pathname: "/build",
                      query: { locale: query.locale || "en" },
                    }}
                    className="flex items-center flex-shrink-0"
                  >
                    {details?.brokerLogo && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.15 }}
                        className="hidden lg:flex items-center gap-2"
                      >
                        <img
                          src={details?.brokerLogo}
                          className="max-h-[26px] w-auto max-w-[110px] object-contain"
                          alt="Logo"
                        />
                        {!details?.isWhiteLabled && (
                          <Badge className="text-[9px] font-semibold py-0">
                            <Link href="/subscription">
                              {userType === "Pro"
                                ? "Pro"
                                : userType === "Free_access"
                                  ? "Free Access"
                                  : "Free"}
                            </Link>
                          </Badge>
                        )}
                      </motion.div>
                    )}
                  </Link>
                  <Button
                    onClick={() => setShowSidebar(false)}
                    variant="ghost"
                    className="flex items-center justify-center hidden lg:flex w-8 h-8 p-0 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors duration-150"
                  >
                    <FiSidebar size={18} />
                  </Button>
                </>
              ) : (
                /* Collapsed mode: Always show the expand sidebar button clearly */
                <div className="w-full h-8 flex items-center justify-center">
                  <Button
                    onClick={() => setShowSidebar(true)}
                    variant="ghost"
                    className="w-8 h-8 p-0 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md flex items-center justify-center"
                    title={t("Expand Sidebar")}
                  >
                    <PanelLeftOpen size={18} />
                  </Button>
                </div>
              )}
            </div>
            {/* Sidebar Menu */}
            <ul className="flex flex-col gap-2 cursor-pointer w-full mt-8">
              <SidebarItem
                href={`/build?locale=${query.locale || "en"}`}
                icon={<SquarePen size={15} />}
                label={t("Create")}
                active={pathname.includes("build")}
              />
              <SidebarItem
                href={`/test?locale=${query.locale || "en"}`}
                icon={<Gauge size={15} />}
                label={t("Test")}
                active={
                  (pathname === "/test" && query.tab != "scanners") ||
                  pathname.includes("strategy_details")
                }
              />
              <SidebarItem
                href={`/deploy?locale=${query.locale || "en"}`}
                icon={<Rocket size={15} />}
                label={t("Deploy")}
                active={
                  pathname.includes("deploy") || pathname.includes("forward")
                }
              />
              {details?.whiteLableOptions?.brokerConnectionModal !==
                "hidden" && (
                <SidebarItem
                  icon={<Unplug size={15} />}
                  label={t("Connect API")}
                  onClick={() => setShowConnectForm({ isOpen: true })}
                />
              )}

              <SidebarItem
                href={`/marketplace?locale=${query.locale || "en"}`}
                icon={<BsBarChart size={15} />}
                label={t("Marketplace")}
                active={pathname.includes("marketplace")}
              />

              {!details?.isWhiteLabled && (
                <SidebarItem
                  href={`/subscription?locale=${query.locale || "en"}`}
                  icon={<CreditCard size={15} />}
                  label={t("Subscription")}
                  active={pathname.includes("subscription")}
                />
              )}

              {/* More Menu */}
              {(() => {
                const isMoreActive =
                  (pathname === "/test" && query.tab == "scanners") ||
                  pathname === "/broker-stats-v2" ||
                  pathname.startsWith("/admin");

                return (
                  <Menubar className="flex flex-col h-fit border-none p-0 items-start w-full justify-start">
                    <MenubarMenu>
                      <HoverCard openDelay={0}>
                        <HoverCardTrigger asChild>
                          <MenubarTrigger
                            className={cn(
                              "flex w-full p-2 bg-card cursor-pointer gap-5 items-center text-sm rounded-[6px] hover:bg-primary hover:text-primary-foreground",
                              showSidebar ? "pl-5 justify-start" : "justify-center",
                              isMoreActive && "bg-primary text-primary-foreground",
                            )}
                          >
                            <ChartNoAxesGantt size={15} />
                            {showSidebar && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="transition-opacity duration-200 ease-in-out bg-transparent"
                              >
                                {t("More")}
                              </motion.div>
                            )}
                          </MenubarTrigger>
                        </HoverCardTrigger>
                        {!showSidebar && (
                          <HoverCardContent className="w-auto ml-[52px] mt-[-20px] p-2 font-medium z-[1000]">
                            {t("More")}
                          </HoverCardContent>
                        )}
                      </HoverCard>
                      <MenubarContent>
                        <Link href="/test?tab=scanners">
                          <MenubarItem>{t("Scanners")}</MenubarItem>
                        </Link>
                        {!details?.isWhiteLabled && (
                          <>
                            <Link href="/broker-stats-v2">
                              <MenubarItem>{t("Broker Dashboard")}</MenubarItem>
                            </Link>

                            <Link
                              href="https://www.youtube.com/@trylevel2/videos"
                              target="_blank"
                            >
                              <MenubarItem>{t("Explainer Videos")}</MenubarItem>
                            </Link>
                          </>
                        )}
                        {user?.is_admin && (
                          <MenubarItem
                            onClick={() =>
                              push(
                                `/admin/dashboard?locale=${query.locale || "en"}`,
                              )
                            }
                          >
                            {t("Admin Dashboard")}
                          </MenubarItem>
                        )}

                        {!details?.isWhiteLabled && (
                          <MenubarItem>
                            <Link
                              href="https://help.trylevel2.com/"
                              target="_blank"
                            >
                              {t("Help Center")}
                            </Link>
                          </MenubarItem>
                        )}
                        <MenubarItem
                          onClick={() =>
                            setShowTechnicalRequest({
                              isOpen: true,
                              isRequest: false,
                              isContactSales: false,
                              helpline: true,
                            })
                          }
                        >
                          {t("Technical support")}
                        </MenubarItem>
                        <MenubarItem onClick={Logout}>
                          {t("Logout")}
                        </MenubarItem>
                      </MenubarContent>
                    </MenubarMenu>
                  </Menubar>
                );
              })()}
            </ul>
          </div>

          {/* Footer */}
          <AnimatePresence>
            {showSidebar && <SidebarCarousel />}
          </AnimatePresence>
        </div>
      </motion.nav>
    </>
  );
}
